import { useTranslation } from 'react-i18next'

import { useTheme } from 'styled-components'

import { Button, ButtonWithSpinner, Drawer, InputWithLabel, Text, TextAreaRichEditor } from '@reveni-io/reveni-ui'

import { getEmailVariables, isHtmlContentEmpty } from '@/common/helpers/helpers'
import AddLinkPopover from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/Content/AddLinkPopover/AddLinkPopover'
import {
  getLinkableVariables,
  getSectionsWithLinkableVariables,
} from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/Content/helpers'

import RichTextEditor from '../RichTextEditor/RichTextEditor'
import AiFlowActions from './AiFlowActions/AiFlowActions'
import useEditorDisabled from './hooks/useEditorDisabled/useEditorDisabled'
import useEmailEditor from './hooks/useEmailEditor/useEmailEditor'
import { aiFlowStates, emailToolbarConfig } from './constants'
import * as Styles from './styles'

const SendEmailDrawer = ({
  show,
  onClose,
  copies,
  emailType,
  to,
  setTo,
  cc,
  setCc,
  replyTo,
  setReplyTo,
  subject,
  setSubject,
  body,
  setBody,
  onSendEmail,
  loading,
  onGenerateAiEmail,
  generatingAiEmail,
  aiEmailGenerated,
  aiFlowState,
  userInquiry,
  setUserInquiry,
  onAcceptAiContent,
  onRequestImprovement,
  onDiscardAiContent,
  onCancelGeneration,
  aiEmailGenerationEnabled,
  merchantId,
}) => {
  const theme = useTheme()

  const { t } = useTranslation()
  const isAiModeActive = aiFlowState && aiFlowState !== aiFlowStates.initial
  const aiEditorRef = useEditorDisabled(isAiModeActive)

  const handleBodyChange = data => {
    setBody(isHtmlContentEmpty(data.html) ? '' : data.html)
  }

  const {
    editorRef,
    toolbarRef,
    onAddVariable,
    showLinkPopup,
    openAddLinkPopup,
    onCloseLinkPopup,
    onAddLink,
    onImageUpload,
    triggerImageUpload,
    availableVariables,
    availableSections,
  } = useEmailEditor({ onChange: handleBodyChange, merchantId, disabled: isAiModeActive, emailType })

  const isFormValid = () => {
    return to && subject && body && replyTo
  }

  const getAiButtonLabel = () => {
    if (aiFlowState === aiFlowStates.generating || aiFlowState === aiFlowStates.improving) {
      return t('Generando...')
    }

    return subject?.trim() || body?.trim() ? t('Mejorar con Reveni IA') : t('Generar con Reveni AI')
  }

  return (
    <Drawer
      show={show}
      onClose={onClose}
      size="l"
      options={{ showHeaderActions: false }}
      confirmClose
      confirmCloseTitle={t('Cambios sin guardar')}
      confirmCloseText={t('Tienes cambios sin guardar que se perderán al cerrar. ¿Seguro que quieres continuar?')}
      confirmCloseConfirmLabel={t('Descartar cambios')}
      confirmCloseCancelLabel={t('Seguir editando')}
    >
      <Styles.MainContainer>
        <Text type="titleM">{t(copies.title)}</Text>
        <Styles.SectionsContainer>
          <InputWithLabel fullWidth label={t(copies.to)} value={to} onChange={e => setTo(e.target.value)} />
          <InputWithLabel
            fullWidth
            label={t('Destinatarios en copia (Cc:)')}
            placeholder={t('Añade más direcciones de email separándolas por comas (,)')}
            value={cc}
            onChange={e => setCc(e.target.value)}
          />
          <InputWithLabel
            fullWidth
            label={t(copies.replyTo)}
            value={replyTo}
            onChange={e => setReplyTo(e.target.value)}
          />
        </Styles.SectionsContainer>
        <Styles.MessageSectionsContainer aiMode={isAiModeActive}>
          <Text type="titleS">{t('Mensaje')}</Text>
          <InputWithLabel
            fullWidth
            label={t('Asunto')}
            value={subject}
            onChange={e => setSubject(e.target.value)}
            loading={generatingAiEmail}
            thinking={generatingAiEmail}
            generated={aiEmailGenerated}
            backgroundColor={theme.colors.background.primary}
            disabled={isAiModeActive}
          />
          {isAiModeActive ? (
            <TextAreaRichEditor
              label={t('Contenido')}
              fullWidth
              value={body}
              textType="html"
              minHeight="64px"
              onChange={handleBodyChange}
              editorRef={aiEditorRef}
              loading={generatingAiEmail}
              thinking={generatingAiEmail}
              generated={aiEmailGenerated}
              backgroundColor={theme.colors.background.primary}
              disabled={isAiModeActive}
            />
          ) : (
            <>
              <RichTextEditor
                value={body}
                onChange={handleBodyChange}
                config={emailToolbarConfig}
                minHeight="200px"
                toolbarRef={toolbarRef}
                editorRef={editorRef}
                variables={getEmailVariables(availableVariables)}
                onAddLink={openAddLinkPopup}
                onAddImage={triggerImageUpload}
                onAddVariable={onAddVariable}
                availableVariables={availableVariables}
                availableSections={availableSections}
                onImageUpload={onImageUpload}
              />
              <AddLinkPopover
                toolbarRef={toolbarRef}
                availableVariables={getLinkableVariables(availableVariables)}
                availableSections={getSectionsWithLinkableVariables(availableVariables, availableSections)}
                show={showLinkPopup}
                onClose={onCloseLinkPopup}
                onAddLink={onAddLink}
              />
            </>
          )}
          <AiFlowActions
            aiFlowState={aiFlowState}
            aiButtonLabel={getAiButtonLabel()}
            onGenerateAiEmail={onGenerateAiEmail}
            generatingAiEmail={generatingAiEmail}
            onAcceptAiContent={onAcceptAiContent}
            onRequestImprovement={onRequestImprovement}
            onDiscardAiContent={onDiscardAiContent}
            userInquiry={userInquiry}
            setUserInquiry={setUserInquiry}
            onCancelGeneration={onCancelGeneration}
            aiEmailGenerationEnabled={aiEmailGenerationEnabled}
            disabled={isAiModeActive}
          />
        </Styles.MessageSectionsContainer>
        <Styles.ActionsContainer>
          <Button fullWidth variant="outlined" onClick={onClose}>
            {t('Cancelar')}
          </Button>
          <ButtonWithSpinner
            fullWidth
            defaultLabel={t('Enviar')}
            loading={loading}
            loadingLabel={t('Enviar')}
            disabled={loading || !isFormValid() || isAiModeActive}
            onClick={onSendEmail}
          ></ButtonWithSpinner>
        </Styles.ActionsContainer>
      </Styles.MainContainer>
    </Drawer>
  )
}

export default SendEmailDrawer
